import { TimelineEntry, TimelineItem } from "../../types/timeline";
import { Stop } from "../../types/stops";
import { TrackEntryAndData } from "../../types/track";
import { Timestamp } from "../../types/core";

export const createTimelineItemsFromTracksAndEntries = (trackEntries: TrackEntryAndData[], timelineEntries: TimelineEntry[]): TimelineItem[] => {
    if (!trackEntries) {
        return [];
    }

    let timelineIndex = 0;

    const fillEventsUntil = (endTs: Timestamp): TimelineEntry[] => {
        const events = [] as TimelineEntry[];
        while (timelineIndex < timelineEntries.length && timelineEntries[timelineIndex].tsStart <= endTs) {
            events.push(timelineEntries[timelineIndex]);
            timelineIndex += 1;
        }
        return events;
    }

    const items = [] as TimelineItem[];

    if ((timelineEntries.length > 0 && trackEntries.length === 0)
        || (timelineEntries.length > 0 && timelineEntries[0].tsStart < trackEntries[0].entry.startTs)) {
        const startTs = timelineEntries[0].tsStart;
        const endTs = trackEntries.length > 0
            ? trackEntries[0].entry.startTs
            : timelineEntries[timelineEntries.length - 1].tsEnd;
        const events = fillEventsUntil(endTs);
        items.push({
            start: startTs,
            stop: {
                start: startTs,
                end: endTs
            } as Stop,
            events: events
        } as TimelineItem);
    }

    for (let i = 0; i < trackEntries.length; i++) {
        if (i > 0) {
            const startTs = trackEntries[i - 1].entry.endTs;
            const endTs = trackEntries[i].entry.startTs;
            const events = fillEventsUntil(endTs);
            items.push({
                start: startTs,
                stop: {
                    start: startTs,
                    end: endTs
                } as Stop,
                events: events
            } as TimelineItem);
        }
        const startTs = trackEntries[i].entry.startTs;
        const endTs = trackEntries[i].entry.endTs;
        const events = fillEventsUntil(endTs);
        items.push({ start: startTs, track: trackEntries[i], events: events });
    }

    if (timelineEntries.length > 0
        && trackEntries.length > 0
        && timelineEntries[timelineEntries.length - 1].tsStart > trackEntries[trackEntries.length - 1].entry.startTs) {
        const startTs = trackEntries[trackEntries.length - 1].entry.endTs;
        const endTs = timelineEntries[timelineEntries.length - 1].tsEnd;
        const events = fillEventsUntil(endTs);
        items.push({
            start: startTs,
            stop: {
                start: startTs,
                end: endTs
            } as Stop,
            events: events
        } as TimelineItem);
    }

    return items;
};

export function getFirstWeekday(locale = navigator.language): number {
    // Try modern API first (Chrome, Edge, Safari 16.4+)
    const loc = new Intl.Locale(locale);
    if (typeof (loc as any).getWeekInfo === 'function') {
        const weekInfo = (loc as any).getWeekInfo();
        if (weekInfo && typeof weekInfo.firstDay === 'number') {
            // Intl.Locale weekInfo.firstDay: 1=Monday, 2=Tuesday, ..., 7=Sunday
            // JavaScript Date.getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
            // Convert: 7 -> 0, 1-6 -> 1-6
            return weekInfo.firstDay === 7 ? 0 : weekInfo.firstDay;
        }
    }
    
    // Use locale-based inference for browsers without getWeekInfo support
    const localeCode = locale.split('-')[0].toLowerCase();
    const region = locale.split('-')[1]?.toLowerCase();
    
    // Locales that typically start week on Sunday
    const sundayFirstLocales = ['en', 'ja', 'ko', 'th', 'he', 'ar'];
    const sundayFirstRegions = ['us', 'ca', 'ph', 'tw', 'il'];
    
    if (sundayFirstLocales.includes(localeCode) || 
        (localeCode === 'en' && sundayFirstRegions.includes(region || ''))) {
        return 0; // Sunday
    }
    
    return 1; // Monday (default for most locales)
}